<template>
  <v-alert
    v-if="show"
    color="blue25 text-blue900"
    class="extension-install-alert text-body-2 text-md-center"
    closable
  >
    <span class="d-inline-flex align-center extension-install-alert__content">
      <PhPuzzlePiece class="text-h5 text-sm-h6 flex-shrink-0 mr-2 text-blue400" weight="fill" />
      <span class="extension-install-alert__text">
        {{ $t('component.extension_install_banner.message') }}
        <a
          :href="installUrl"
          target="_blank"
          rel="noopener"
          class="extension-install-alert__link text-blue600 font-weight-semibold ml-1 cursor-pointer"
        >{{ $t('component.extension_install_banner.cta') }} <PhArrowSquareOut class="text-body-2 ml-1" style="margin-bottom: -2px" /></a>
      </span>
    </span>
    <template #close>
      <BrandButton
        size="small"
        icon
        :aria-label="$t('component.extension_install_banner.dismiss_aria')"
        @click="dismiss"
      >
        <PhX class="text-h6" />
      </BrandButton>
    </template>
  </v-alert>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { PhPuzzlePiece, PhArrowSquareOut, PhX } from '@phosphor-icons/vue'
import { useDeviceAware } from '@/composables/useDeviceAware'
import { useExtensionPresence } from '@/composables/useExtensionPresence'
import { resolveInstallLink } from '@/utils/extension-install'
import Preferences from '@/plugins/preferences'

const DISMISS_KEY_PREFIX = 'extensionInstallNoticeDismissed'

const store = useStore()
const { isWebApp, isDesktop, deviceInfo } = useDeviceAware()
const { detected, checkPresence } = useExtensionPresence()

// Hidden by default; only revealed once the probe confirms the extension is
// missing, so users who have it never see a flash of the banner.
const dismissed = ref(true)
const probed = ref(false)

const userId = computed(() => store.getters['auth/userId'])

const installLink = computed(() => resolveInstallLink({
  browser: deviceInfo.value.browser,
  os: deviceInfo.value.os,
  isDesktop: isDesktop.value,
  isMobile: false,
}))
const installUrl = computed(() => installLink.value?.url ?? null)

const dismissKey = () => `${DISMISS_KEY_PREFIX}:${userId.value}`

const show = computed(() =>
  isWebApp.value
  && isDesktop.value
  && !!installUrl.value
  && probed.value
  && !detected.value
  && !dismissed.value
)

const dismiss = async () => {
  dismissed.value = true
  if (userId.value) await Preferences.set(dismissKey(), true)
}

// Probe once, when a logged-in web desktop user with an installable browser is
// known. Clicking the install CTA opens the store but does NOT dismiss — only
// the close button persists dismissal, so the nudge survives a bounce.
let initialized = false
const init = async () => {
  if (initialized) return
  if (!isWebApp.value || !isDesktop.value || !installUrl.value || !userId.value) return
  initialized = true

  if ((await Preferences.get(dismissKey())) === true) return

  dismissed.value = false
  await checkPresence()
  probed.value = true
}

watch(userId, init, { immediate: true })
</script>

<style lang="scss" scoped>
.extension-install-alert :deep(.v-alert__wrapper) {
  align-items: center;
}

.extension-install-alert :deep(.v-alert__content) {
  min-width: 0;
}

.extension-install-alert__content,
.extension-install-alert__text {
  min-width: 0;
}

.extension-install-alert__link {
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    color: rgb(var(--v-theme-blue800));
  }
}
</style>
